import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Department, useDepartment } from '@/entities/department'; // ★ FSD
import { useCurrentOrganization } from '@/entities/organization'; // ★ FSD
import type { MemberData } from '@/entities/organization'; // <-- Add import from entity
import { useManageMemberDepartments } from '@/features/organization-member-dept-manage'; // ★ FSD
import { MemberDepartmentTable } from '@/widgets/member-department-table'; // ★ FSD

// import { useAuth } from '@clerk/clerk-react'; // isAuthLoaded は useViewer で代替可
import styles from './styles.module.css'; // ★ CSS Modules ファイル名を修正

// ★ OrganizationSettings コンポーネント (元々の関数名と同じ)
export const OrganizationSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  // ★ エンティティ・フィーチャーフックを使用
  const {
    organization,
    membership,
    isLoaded: isOrgLoaded,
    isAdmin,
  } = useCurrentOrganization();
  const {
    selectedDepartment,
    userDepartments,
    isLoading: isDeptLoading,
    error: deptError,
  } = useDepartment();
  const { saving, message, saveAssignments } = useManageMemberDepartments();

  // ページ固有の State
  const [allDepartments, setAllDepartments] = useState<Department[]>([]);
  const [members, setMembers] = useState<MemberData[]>([]); // ★ MemberData 型を使用
  const [localLoading, setLocalLoading] = useState(true); // メンバー/部署データ読み込み
  const [changedMemberIds, setChangedMemberIds] = useState<Set<string>>(
    new Set()
  );
  const [isDeptAssignmentChanged, setIsDeptAssignmentChanged] = useState(false);

  const prevOrgIdRef = useRef<string | null>(null);

  // 組織全体の部署リストとメンバーリスト取得 (管理者のみ)
  useEffect(() => {
    // organization, membership が利用可能になったら実行
    if (organization && isOrgLoaded && membership) {
      // 組織が変わった場合のみ再取得
      if (prevOrgIdRef.current === organization.id) {
        if (localLoading) setLocalLoading(false);
        return;
      }
      prevOrgIdRef.current = organization.id;
      setLocalLoading(true);
      setChangedMemberIds(new Set());
      setIsDeptAssignmentChanged(false);
      setMessageLocal({ type: '', text: '' }); // メッセージクリア (setMessageLocal を使う)

      // 全部署リスト取得 (publicMetadata から)
      const allDeptsData = (organization.publicMetadata?.departments ||
        {}) as Record<string, Omit<Department, 'id'>>;
      const deptsArray: Department[] = Object.entries(allDeptsData).map(
        ([id, data]) => ({
          id,
          name: data?.name || id,
          address: data?.address,
          phone: data?.phone,
          fax: data?.fax,
          postal_code: data?.postal_code,
        })
      );
      setAllDepartments(deptsArray);

      // メンバーリスト取得 (管理者のみ)
      const fetchMembers = async () => {
        if (isAdmin) {
          try {
            const memberList = await organization.getMemberships();
            const activeMembers: MemberData[] = memberList.data.map((mem) => ({
              userId: mem.publicUserData?.userId ?? 'unknown',
              identifier: mem.publicUserData?.identifier || 'N/A',
              firstName: mem.publicUserData?.firstName || '',
              lastName: mem.publicUserData?.lastName || '',
              role: mem.role,
              deptIds: (mem.publicMetadata?.deptIds || []) as string[],
              membershipId: mem.id,
            }));
            setMembers(activeMembers);
          } catch (error: unknown) {
            console.error('Failed to fetch members:', error);
            setMessageLocal({
              type: 'error',
              text: 'メンバーリストの取得に失敗しました。',
            });
          }
        } else {
          setMembers([]);
        }
      };

      fetchMembers().finally(() => {
        setLocalLoading(false);
      });
    } else if (!isOrgLoaded) {
      setLocalLoading(true);
    } else {
      // 組織情報がない場合の状態リセット
      setAllDepartments([]);
      setMembers([]);
      setLocalLoading(false);
    }
  }, [organization, isOrgLoaded, membership, isAdmin, localLoading]); // localLoading を依存配列に含めるのは注意が必要かも

  // メンバー部署割り当て変更ハンドラ (UI 状態更新)
  const handleMemberDeptChange = (
    userId: string,
    deptId: string,
    isChecked: boolean
  ) => {
    setMembers((prevMembers) =>
      prevMembers.map((member) => {
        if (member.userId === userId) {
          const currentDeptIds = new Set(member.deptIds || []);
          if (isChecked) {
            currentDeptIds.add(deptId);
          } else {
            currentDeptIds.delete(deptId);
          }
          return { ...member, deptIds: Array.from(currentDeptIds) };
        }
        return member;
      })
    );
    setChangedMemberIds((prev) => new Set(prev).add(userId));
    setIsDeptAssignmentChanged(true);
  };

  // 保存ボタンクリックハンドラ
  const handleSaveClick = () => {
    saveAssignments(members, changedMemberIds).then(() => {
      // 保存成功後、変更状態をリセット
      if (message.type === 'success') {
        // useManageMemberDepartments の message を参照
        setChangedMemberIds(new Set());
        setIsDeptAssignmentChanged(false);
        // 成功メッセージを一定時間後に消す処理は useManageMemberDepartments 側でも良い
        setTimeout(() => {
          setMessageLocal({ type: '', text: '' });
        }, 3000);
      }
    });
  };

  // ★ メッセージ表示用にローカル state を使う (フックの message と分ける)
  const [messageLocal, setMessageLocal] = useState({ type: '', text: '' });
  useEffect(() => {
    setMessageLocal(message); // フックからのメッセージをローカルにコピー
  }, [message]);

  // ★ ローディング表示
  const combinedLoading = !isOrgLoaded || isDeptLoading || localLoading;
  if (combinedLoading) {
    return (
      <div className={styles.loadingContainerCenter}>
        <p>読み込み中...</p>
      </div>
    ); // CSS Modules
  }

  // ★ エラー表示
  if (deptError) {
    return <div className={styles.errorMessage}>エラー: {deptError}</div>; // CSS Modules
  }
  if (!organization || !membership) {
    return (
      <div className={styles.organizationSettingsContainer}>
        {' '}
        {/* CSS Modules */}
        <p>組織が選択されていないか、アクセス権がありません。</p>
        <button onClick={() => navigate('/')}>ホームに戻る</button>
      </div>
    );
  }

  // ヘルパー関数
  const getCurrentUserDeptNames = () => {
    if (!userDepartments || userDepartments.length === 0) return '所属なし';
    return userDepartments.map((dept) => dept.name).join(', ');
  };

  return (
    // ★ CSS Modules を適用
    <div
      className={styles.organizationPageScrollBox}
      data-organization-settings-container="true"
    >
      <div className={styles.organizationPage}>
        <h1 className={styles.sectionHeading}>
          組織設定 ({organization.name})
        </h1>

        {messageLocal.text && (
          <div className={`${styles.message} ${styles[messageLocal.type]}`}>
            {messageLocal.text}
          </div>
        )}

        <form
          onSubmit={(e) => e.preventDefault()}
          className={styles.profileForm}
        >
          <div className={styles.settingsSection}>
            <h3 className={styles.sectionHeading}>
              あなたの所属クリニック情報
            </h3>
            <div className={styles.formGroup}>
              <span>クリニック名:</span>
              <div>{getCurrentUserDeptNames()}</div>
            </div>

            <div className={styles.formGroup}>
              <span>郵便番号:</span>
              <div>{selectedDepartment?.postal_code || '-'}</div>
            </div>

            <div className={styles.formGroup}>
              <span>住所:</span>
              <div>{selectedDepartment?.address || '-'}</div>
            </div>

            <div className={styles.formGroup}>
              <span>電話:</span>
              <div>{selectedDepartment?.phone || '-'}</div>
            </div>

            <div className={styles.formGroup}>
              <span>FAX:</span>
              <div>{selectedDepartment?.fax || '-'}</div>
            </div>
          </div>

          {isAdmin && (
            <div className={styles.settingsSection}>
              <h3 className={styles.sectionHeading}>
                メンバー所属クリニック (管理者用)
              </h3>

              <p className={styles.description}>
                各メンバーが所属するクリニックをチェックボックスで選択し、「所属部署の変更を保存」ボタンで更新します。
              </p>

              {/* ★ MemberDepartmentTable ウィジェットを使用 */}
              <MemberDepartmentTable
                members={members}
                departments={allDepartments}
                onAssignmentChange={handleMemberDeptChange}
                disabled={saving}
              />
              <div
                className={`${styles.buttonGroupInline} ${styles.buttonGroupSaveDept}`}
              >
                <button
                  type="button"
                  onClick={handleSaveClick} // ★ 修正: 保存ハンドラ呼び出し
                  disabled={!isDeptAssignmentChanged || saving}
                  className={styles.saveDeptButton}
                >
                  {saving ? '保存中...' : '所属部署の変更を保存'}
                </button>
              </div>
            </div>
          )}
          {/* ... (管理者以外の表示やデータなしの場合の表示) ... */}
          {!isAdmin && (
            <p className={styles.infoMessage}>
              メンバー管理機能は管理者のみ利用可能です。
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default OrganizationSettingsPage;
